// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconPresentationProps } from "@insite/mobius/Icon";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { styledScrollbar } from "@rc/App/SharedStyles/sharedStyles";
import { color, xScreen } from "@rc/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

export interface TippyPopupStyle {
    main?: InjectableCss;
    headerMenuMobile?: InjectableCss;
    elementWrapper?: React.CSSProperties;
    closeButton?: IconPresentationProps;
}

export const mobileTippyBreakpoint = "md";

// we add it to `preStyle/globalStyle`
export const mobileTippyStyle = css`
    ${xScreen("lte", mobileTippyBreakpoint)} {
        div[data-tippy-root].tippy-menu-left-side {
            width: 100%;
            max-width: 100%;
            transition: transform 150ms ease-in-out;
            top: var(--tippy-top-offset) !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            overflow-y: visible;
            overflow-x: hidden;
            z-index: ${getProp("theme.zIndex.modal")} !important;
            .tippy-box {
                border: none;
                box-shadow: none;
            }

            .tippy-arrow {
                display: none;
            }
        }

        .tippy-content {
            background: ${color("common.ui1")};
            padding: 0;
            & > div {
                height: 100%;
            }
        }
    }
`;

export const tippyPopupStyles: TippyPopupStyle = {
    main: {
        css: css`
            &.tippy-box {
                transition-duration: 500ms !important;
                color: ${color("text.main")};
                background-color: ${color("common.ui1")};
                border: 1px solid ${color("common.border")};
                box-shadow: ${getProp("theme.shadows.4")};
                min-width: 200px;

                &:focus,
                & > *:focus {
                    outline: none;
                }

                & > .tippy-content {
                    padding: 20px;
                    ${styledScrollbar(2)}
                    max-height: calc(100vh - 100px);
                    overflow-y: auto;
                }

                &&&[data-placement^="top"] > .tippy-arrow {
                    bottom: -19px;
                    &:before {
                        transform: rotate(45deg);
                    }
                }

                &&&[data-placement^="bottom"] > .tippy-arrow {
                    top: -19px;
                    &:before {
                        transform: rotate(-135deg);
                    }
                }

                &&& .tippy-arrow,
                &&& .tippy-arrow::before {
                    position: absolute;
                    width: 13px;
                    height: 13px;
                    z-index: 1;
                }

                &&& .tippy-arrow::before {
                    content: "";
                    background: ${color("common.ui1")};
                    border-color: ${color("common.border")};
                    border-style: solid;
                    border-width: 0 1px 1px 0;
                    top: 0;
                }
            }
        `,
    },
    elementWrapper: {
        outline: "none !important",
    },
    headerMenuMobile: {
        css: css`
            background: ${color("primary")};
            position: relative;
            top: -${({ paddingTop }: any) => paddingTop}px;
            left: -${({ paddingLeft }: any) => paddingLeft}px;
            width: calc(100% + ${({ paddingLeft, paddingRight }: any) => paddingLeft + paddingRight}px);
            padding: 5px 20px;
            img {
                max-height: 48px;
            }
        `,
    },
    closeButton: {
        size: 14,
        src: "X",
        color: "common.ui10",
        css: css`
            cursor: pointer;
            position: absolute;
            right: 10px;
            top: 10px;
            z-index: 999;
            display: none;

            &&&& {
                @media print {
                    display: none;
                }
            }

            &:hover {
                color: ${color("danger")};
            }

            &.both {
                display: flex;
            }

            &.desktop {
                ${xScreen("gt", mobileTippyBreakpoint)} {
                    display: flex;
                }
            }

            &.mobile {
                ${xScreen("lte", mobileTippyBreakpoint)} {
                    display: flex;
                }
            }
        `,
    },
};
